import type { ReactNode } from "react"
import { createContext, useContext, useState, useEffect, useRef } from "react"
import { useNavigate, useRouterState } from "@tanstack/react-router"
import { stepNameToNumber, stepNumberToName, isValidStep } from "../utils/step-mapping"
import { useNbiFormContextSafe } from "./NbiFormContext"

interface NbiNavigationContextType {
    currentStep: number
    totalSteps: number
    isSubmitStep: boolean
    stepNames: string[]
    setCurrentStep: (step: number) => void
    handleNext: () => void
    handlePrevious: () => void
    handleSubmit: () => void
    updateUrlWithAppId: (appId: number) => void
}

export const NbiNavigationContext = createContext<NbiNavigationContextType | undefined>(undefined)

interface NbiNavigationProviderProps {
    children: ReactNode
    totalSteps: number
    stepNames: string[]
    onSubmit: () => void
}

export const NbiNavigationProvider = ({ children, totalSteps, stepNames, onSubmit }: NbiNavigationProviderProps) => {
    let navigate: ReturnType<typeof useNavigate> | null = null
    let routerState: ReturnType<typeof useRouterState> | null = null
    let search: { step?: string | number; appId?: number } = {}

    try {
        navigate = useNavigate()
        routerState = useRouterState()
        if (routerState) {
            search = (routerState.location.pathname.endsWith('/agent/application') ? routerState.location.search : {}) as { step?: string | number; appId?: number }
        }
    } catch { }

    const formContext = useNbiFormContextSafe()
    const formApplicationId = formContext?.form?.state?.values?.applicationId as number | undefined

    const isInitialMount = useRef(true)
    const isUpdatingFromUrl = useRef(false)
    const appIdRef = useRef<number | undefined>(search.appId || formApplicationId)
    const hadStepOnMount = useRef(!!search?.step)

    const [currentStep, setCurrentStep] = useState(() => {
        if (search?.step) {
            const stepNum = stepNameToNumber(search.step)
            if (isValidStep(stepNum)) {
                return stepNum
            }
        }
        return 0
    })

    useEffect(() => {
        if (formApplicationId && formApplicationId !== appIdRef.current) {
            appIdRef.current = formApplicationId
            if (navigate && search?.step) {
                const stepName = stepNumberToName(currentStep)
                navigate({
                    to: '/agent/application',
                    search: { step: stepName, appId: formApplicationId },
                    replace: true,
                }).catch(() => { })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formApplicationId, navigate, search?.step])

    useEffect(() => {
        if (!navigate) return
        if (!hadStepOnMount.current) return

        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }

        if (isUpdatingFromUrl.current) {
            isUpdatingFromUrl.current = false
            return
        }

        const stepName = stepNumberToName(currentStep)

        navigate({
            to: '/agent/application',
            search: { step: stepName, appId: appIdRef.current },
            replace: true,
        }).catch(() => { })
    }, [currentStep, navigate])

    useEffect(() => {
        if (!routerState) return

        if (routerState.location.pathname === '/agent/application' && search?.step && !isInitialMount.current) {
            const stepNum = stepNameToNumber(search.step)
            if (isValidStep(stepNum) && stepNum !== currentStep) {
                isUpdatingFromUrl.current = true
                setCurrentStep(stepNum)
            }
        }
    }, [routerState?.location.pathname, routerState?.location.search, search?.step, currentStep])

    const handleNext = () => {
        if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleSubmit = () => {
        onSubmit()
    }

    const updateUrlWithAppId = (appId: number) => {
        appIdRef.current = appId
        if (navigate) {
            const stepName = stepNumberToName(currentStep)
            navigate({
                to: '/agent/application',
                search: { step: stepName, appId },
                replace: true,
            }).catch(() => { })
        }
    }

    const isSubmitStep = currentStep === 11

    const value = {
        currentStep,
        totalSteps,
        isSubmitStep,
        stepNames,
        setCurrentStep,
        handleNext,
        handlePrevious,
        handleSubmit,
        updateUrlWithAppId,
    }

    return <NbiNavigationContext.Provider value={value}>{children}</NbiNavigationContext.Provider>
}

export const useNbiNavigation = () => {
    const context = useContext(NbiNavigationContext)
    if (!context) {
        throw new Error("useNbiNavigation must be used within an NbiNavigationProvider")
    }
    return context
}

export const useNbiNavigationSafe = () => {
    return useContext(NbiNavigationContext)
}
